import { Injectable } from '@angular/core';

const MULTIFORM_NAVIGATION_HANDLER = 'rxEventMultiformNavigation';

export type MultiformNavigationEvent =
  | 'opened'
  | 'step_changed'
  | 'navigation_state_changed'
  | 'completed'
  | 'closed';

export interface MultiformNavigationPayload {
  currentStepIndex: number | null;
  currentStep: number | null;
  totalSteps: number | null;
  ready: boolean;
  navigationBlocked: boolean;
  navigation: {
    back: {
      disabled: boolean;
    };
    next: {
      disabled: boolean;
    };
  };
  formId?: number | null;
  formTemplateId?: number | null;
  scenario?: 'form' | 'template' | 'inbox';
}

export interface MultiformNavigationState {
  source: 'rxphoto-multiform';
  type: 'MULTIFORM_NAVIGATION_STATE';
  event: MultiformNavigationEvent;
  payload: MultiformNavigationPayload;
}

interface WindowWithWebKit extends Window {
  webkit?: {
    messageHandlers?: {
      [MULTIFORM_NAVIGATION_HANDLER]?: {
        postMessage(message: MultiformNavigationState): void;
      };
    };
  };
}

@Injectable({ providedIn: 'root' })
export class MultiformNavigationService {
  private currentStepIndex: number | null = null;
  private ready = false;
  private navigationBlocked = false;
  private opened = false;
  private hasOpened = false;

  public open(stepIndex: number): void {
    this.currentStepIndex = this.normalizeStepIndex(stepIndex);
    this.ready = false;
    this.navigationBlocked = false;
    this.opened = true;
    this.hasOpened = true;
    this.postState('opened');
  }

  public setStep(stepIndex: number): void {
    if (!this.opened) {
      if (!this.hasOpened) {
        this.open(stepIndex);
      }

      return;
    }

    this.currentStepIndex = this.normalizeStepIndex(stepIndex);
    this.postState('step_changed');
  }

  public setReady(ready: boolean): void {
    if (!this.opened) {
      return;
    }

    this.ready = ready;
    this.postState('navigation_state_changed');
  }

  public setNavigationBlocked(blocked: boolean): void {
    if (!this.opened || this.navigationBlocked === blocked) {
      return;
    }

    this.navigationBlocked = blocked;
    this.postState('navigation_state_changed');
  }

  public close(): void {
    if (!this.opened) {
      return;
    }

    this.currentStepIndex = null;
    this.ready = false;
    this.navigationBlocked = true;
    this.postState('closed');
    this.opened = false;
  }

  private postState(event: MultiformNavigationEvent): void {
    const isReady = this.ready && !this.navigationBlocked;

    this.postMessage({
      source: 'rxphoto-multiform',
      type: 'MULTIFORM_NAVIGATION_STATE',
      event,
      payload: {
        currentStepIndex: this.currentStepIndex,
        currentStep: this.currentStepIndex === null ? null : this.currentStepIndex + 1,
        totalSteps: null,
        ready: this.ready,
        navigationBlocked: this.navigationBlocked,
        navigation: {
          back: { disabled: !isReady },
          next: { disabled: !isReady },
        },
      },
    });
  }

  private postMessage(message: MultiformNavigationState): void {
    const nativeHandler = (window as WindowWithWebKit).webkit?.messageHandlers?.[
      MULTIFORM_NAVIGATION_HANDLER
    ];

    if (nativeHandler) {
      nativeHandler.postMessage(message);
      return;
    }

    window.postMessage(message, window.location.origin);
  }

  private normalizeStepIndex(stepIndex: number): number {
    return Number.isInteger(stepIndex) && stepIndex >= 0 ? stepIndex : 0;
  }
}
